import { useEffect, useState } from "react";
import type { DownloadArtifact } from "./types";

const UNITS = ["B", "KB", "MB", "GB"];
const TICK_MS = 30_000;

function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${UNITS[unit]}`;
}

function formatRemaining(ms: number): string {
  if (ms >= 60_000) return `${Math.ceil(ms / 60_000)} min`;
  return `${Math.ceil(ms / 1000)} sec`;
}

function formatClock(at: Date): string {
  return at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function DownloadArtifactView({ artifact }: { artifact: DownloadArtifact }) {
  const expiresMs = new Date(artifact.expires_at).getTime();
  const [now, setNow] = useState(() => Date.now());
  const remainingMs = expiresMs - now;

  // Re-render every 30s to keep the countdown honest, and land exactly on the
  // expiry so the link disappears the moment it dies rather than up to a tick
  // later.
  useEffect(() => {
    if (remainingMs <= 0) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(remainingMs, TICK_MS));
    return () => clearTimeout(timer);
  }, [remainingMs]);

  // The backend blanks `url` once the stated expiry has passed, so a
  // rehydrated conversation never carries a live link past its lifetime.
  const expired = !artifact.url || remainingMs <= 0;

  const facts = [
    artifact.row_count != null && `${artifact.row_count.toLocaleString()} rows`,
    artifact.byte_size != null && formatBytes(artifact.byte_size),
    expired
      ? "link expired"
      : `expires in ${formatRemaining(remainingMs)} (${formatClock(new Date(expiresMs))})`,
  ].filter(Boolean) as string[];

  return (
    <div className="artifact artifact--download">
      {expired ? (
        <span className="artifact-download-expired">
          {artifact.filename} — download link expired, ask for a fresh one.
        </span>
      ) : (
        // The `download` attribute is ignored cross-origin; the presigned URL
        // carries `Content-Disposition: attachment`, which is what makes the
        // browser save rather than display the file.
        <a
          className="artifact-download-link"
          href={artifact.url ?? undefined}
          download={artifact.filename}
          rel="noreferrer"
        >
          ⤓ {artifact.filename}
        </a>
      )}
      <div className="artifact-download-meta">{facts.join(" · ")}</div>
    </div>
  );
}
